export const PAGE_PERMISSIONS = {
  dashboard: "dashboard",
  booking: "booking",
  bookingaccount: "bookingaccount",
  quote: "quote",
  freeInvoice: "freeInvoice",
  invoicing: "invoicing",
  accommodationService: "accommodationService",
  site: "site",
  room: "room",
  customer: "customer",
  prints: "prints",
  statistic: "statistic",
  profitability: "profitability",
  table: "table",
  system: "system",
  company: "company",
  user: "user",
  role: "role",
};

export const TABLE_PERMISSIONS = {
  roomType: "roomType",
  bedType: "bedType",
  roomEquipment: "roomEquipment",
  paymentMethod: "paymentMethod",
  creditcardCircuit: "creditcardCircuit",
  country: "country",
  municipality: "municipality",
  postalCode: "postalCode",
  language: "language",
  floorPreference: "floorPreference",
  arrivalPreference: "arrivalPreference",
  contactType: "contactType",
  documentType: "documentType",
  externalReferenceSource: "externalReferenceSource",
  defaultNote: "defaultNote",
};

export const ROUTE_PAGE_MAP = {
  "/app/dashboard": PAGE_PERMISSIONS.dashboard,
  "/app/bookings": PAGE_PERMISSIONS.booking,
  "/app/booking-accounts": PAGE_PERMISSIONS.bookingaccount,
  "/app/quotes": PAGE_PERMISSIONS.quote,
  "/app/free-invoices": PAGE_PERMISSIONS.freeInvoice,
  "/app/invoicing": PAGE_PERMISSIONS.invoicing,
  "/app/services": PAGE_PERMISSIONS.accommodationService,
  "/app/sites": PAGE_PERMISSIONS.site,
  "/app/rooms": PAGE_PERMISSIONS.room,
  "/app/customers": PAGE_PERMISSIONS.customer,
  "/app/prints": PAGE_PERMISSIONS.prints,
  "/app/statistics": PAGE_PERMISSIONS.statistic,
  "/app/profitability": PAGE_PERMISSIONS.profitability,
  "/app/tables": PAGE_PERMISSIONS.table,
  "/app/system": PAGE_PERMISSIONS.system,
  "/app/company": PAGE_PERMISSIONS.company,
  "/app/users": PAGE_PERMISSIONS.user,
  "/app/roles": PAGE_PERMISSIONS.role,
};

const ROUTE_ORDER = [
  "/app/dashboard",
  "/app/bookings",
  "/app/customers",
  "/app/rooms",
  "/app/booking-accounts",
  "/app/services",
  "/app/quotes",
  "/app/free-invoices",
  "/app/invoicing",
  "/app/prints",
  "/app/statistics",
  "/app/profitability",
  "/app/company",
  "/app/users",
  "/app/sites",
  "/app/roles",
  "/app/tables",
  "/app/system",
];

const LEGACY_PAGE_KEYS = {
  booking: "bookings",
  bookingaccount: "bookingAccounts",
  accommodationService: "services",
  room: "rooms",
  customer: "customers",
  statistic: "statistics",
  site: "sites",
  table: "tables",
  system: "system",
  company: "company",
  user: "users",
  role: "roles",
};

const DEFAULT_PAGE_LEVELS = {
  dashboard: 1,
  booking: 1,
  bookingaccount: 1,
  quote: 1,
  freeInvoice: 1,
  invoicing: 1,
  accommodationService: 1,
  room: 1,
  customer: 1,
  prints: 1,
  statistic: 1,
  profitability: 1,
  site: 1,
  table: 1,
  system: 0,
  company: 0,
  user: 0,
  role: 0,
};

const DEFAULT_TABLE_LEVELS = {
  roomType: 1,
  bedType: 1,
  roomEquipment: 1,
  paymentMethod: 1,
  creditcardCircuit: 1,
  country: 0,
  municipality: 0,
  postalCode: 0,
  bank: 0,
  language: 1,
  floorPreference: 1,
  arrivalPreference: 1,
  contactType: 1,
  documentType: 1,
  externalReferenceSource: 1,
  defaultNote: 1,
};

export const READ_ONLY_PAGE_KEYS = new Set(["dashboard", "prints", "statistic", "profitability", "table"]);
export const UNAUTHORIZED_LABEL = "UTENTE NON ABILITATO";

export function normalizeLevel(value) {
  const level = Number(value || 0);
  if (!Number.isFinite(level) || level < 0) return 0;
  if (level > 3) return 3;
  return Math.floor(level);
}

export function clampPageLevel(pageKey, value) {
  const normalized = normalizeLevel(value);
  return READ_ONLY_PAGE_KEYS.has(pageKey) ? Math.min(1, normalized) : normalized;
}

export const USER_STORAGE_EVENT = "vesta:user-updated";

export function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

export function writeStoredUser(user) {
  try {
    if (user) localStorage.setItem("user", JSON.stringify(user));
    else localStorage.removeItem("user");
  } finally {
    if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(USER_STORAGE_EVENT));
  }
  return user || null;
}

export function isAdministratorUser(user) {
  const role = String(user?.roleLabel || user?.role || "").trim().toLocaleLowerCase("it");
  return role === "administrator" || role === "amministratore";
}

export function getUserPageLevel(user, pageKey) {
  const key = String(pageKey || "").trim();
  if (!key) return 1;
  const pages = user?.permissions?.pages || {};
  const legacyKey = LEGACY_PAGE_KEYS[key] || "";
  const tables = user?.permissions?.tables || {};
  const roleDefault = (key === "system" || key === "company") && isAdministratorUser(user) ? 3 : undefined;
  const rawLevel = pages[key] ?? (legacyKey ? pages[legacyKey] : undefined) ?? (key === "accommodationService" ? tables.accommodationService : undefined) ?? roleDefault ?? DEFAULT_PAGE_LEVELS[key] ?? 0;
  return clampPageLevel(key, rawLevel);
}

export function getUserTableLevel(user, tableKey) {
  const key = String(tableKey || "").trim();
  if (!key) return 0;
  const tables = user?.permissions?.tables || {};
  const systemTableKeys = new Set(["country", "municipality", "postalCode", "bank"]);
  const roleDefault = systemTableKeys.has(key)
    ? getUserPageLevel(user, "system")
    : undefined;
  return normalizeLevel(tables[key] ?? roleDefault ?? DEFAULT_TABLE_LEVELS[key] ?? 0);
}

export function canAccessPage(user, pageKey, requiredLevel = 1) {
  if (!pageKey) return true;
  return getUserPageLevel(user, pageKey) >= requiredLevel;
}

export function canEditPage(user, pageKey) {
  return getUserPageLevel(user, pageKey) >= 2;
}

export function canDeletePage(user, pageKey) {
  return getUserPageLevel(user, pageKey) >= 3;
}

export function canEditTable(user, tableKey) {
  return getUserTableLevel(user, tableKey) >= 2;
}

export function canDeleteTable(user, tableKey) {
  return getUserTableLevel(user, tableKey) >= 3;
}

export function getFirstAllowedRoute(user) {
  const allowedRoute = ROUTE_ORDER.find((route) => canAccessPage(user, ROUTE_PAGE_MAP[route], 1));
  return allowedRoute || "/app/search";
}

export function getRoutePageKey(pathname) {
  const path = String(pathname || "").replace(/\/$/, "");
  return ROUTE_PAGE_MAP[path] || "";
}
